import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

import { ActivatedRoute, Router } from '@angular/router';
import { AdminProductForm } from './product.form';
import { Subject } from 'rxjs';
import { IConfirmationDialogPayload } from '../../../../shared/ui/component/confirmation-dialog/confirmation-dialog-payload.interface';
import { IndirectUploadZoneComponent } from '../../../../shared/form/element/indirect-upload-zone/indirect-upload-zone.component';
import { Language } from '../../../../shared/enum/language';
import { AdminUrlBuilder } from '../../../../shared/admin/api/admin-url-builder';
import { AbstractPresetFormComponent } from '../preset-form.component';
import { CampaignUrlBuilder } from '../../../campaign-util/shared/url-builder';
import { CampaignId } from '../../../campaign-util/shared/campaign-id';
import { EventBus } from '../../../../shared/event/bus';

@Component({
  selector: 'admin-product-form',
  templateUrl: './form.component.html',
  styleUrls: [ './form.component.scss' ],
})
export class AdminProductFormComponent extends AbstractPresetFormComponent implements OnInit, AfterViewInit {

  @Input() deleteSubject: Subject<IConfirmationDialogPayload>;
  @Input() row: any;
  @Output() delete = new EventEmitter();

  @ViewChild('imageUploader', { static: false })
  protected imageUploader: IndirectUploadZoneComponent;

  allLanguages: any = Language;
  languages: any[] = Object.keys(Language).map((lang: string) => {
    return {
      value: lang,
      label: Language[lang],
    };
  });
  productTypes: any = {
    '6x4': [
      {
        label: 'Single photo print',
        value: 'photo',
      },
      {
        label: 'Keyring',
        value: 'keyring',
      },
      // Snow globe prints the same 6x4 sheet as the keyring, so it belongs to that paper size.
      {
        label: 'Snow globe',
        value: 'snowglobe',
      },
    ],
    '6x8': [
      {
        label: 'Single photo print',
        value: 'photo',
      },
      {
        label: 'Clustersheet',
        value: 'clustersheet',
      },
    ],
    'digital': [
      {
        label: 'All photo download',
        value: 'online',
      },
    ],
  };

  productName: any = {};
  productDescription: any = {};
  selectedLanguage: any = {};
  currentLanguage: string = 'en';
  isLanguagePickerOpen: boolean = false;

  addedLanguages: string[] = [];
  isPopulated: boolean = false;
  isEditMode: boolean = false;
  regularProductSizes: any = [
    {
      value: '6x4',
      label: '6x4',
    },
    {
      value: '6x8',
      label: '6x8',
    },
    {
      value: 'digital',
      label: 'Digital',
    },
  ];
  addonProductSizes: any = [
    {
      value: '6x4',
      label: '6x4',
    },
    {
      value: '6x8',
      label: '6x8',
    },
    {
      value: 'digital',
      label: 'Digital',
    },
  ];
  record = {
    image: '',
    imageS3: '',
    name: Object.assign({}, ...Object.keys(Language).map((lang: string) => {
      return { [lang]: '' };
    })),
    description: Object.assign({}, ...Object.keys(Language).map((lang: string) => {
      return { [lang]: '' };
    })),
  };
  productVariants: any = [
    {
      value: 'regular',
      label: 'Regular',
    },
    {
      value: 'addon',
      label: 'Addon',
    },
  ];

  constructor(formBuilder: AdminProductForm,
              httpClient: HttpClient,
              toastrService: ToastrService,
              crudUrlBuilder: AdminUrlBuilder,
              private campaignUrlBuilder: CampaignUrlBuilder,
              router: Router,
              route: ActivatedRoute,
              campaignId: CampaignId,
              eventBus: EventBus) {
    super(formBuilder, httpClient, toastrService, crudUrlBuilder, router, route, campaignId, eventBus);
  }

  ngAfterViewInit(): void {
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.formMode = (isNaN(this.row.id)) ? 'create' : 'update';

    if (this.row && !this.row.id) {
      this.editRow();
    } else {
      this.selectedLanguage = {};
      this.currentLanguage = 'en';
      this.addedLanguages = [ 'en' ];
    }
  }

  editRow() {
    this.isEditMode = !this.isEditMode;
    if (!this.isPopulated && this.formMode === 'update') {
      this.isPopulated = true;
      this.record = { ...this.row };
      this.form.patchValue(this.record);
      try {
        this.productName = JSON.parse(this.row.name);
      } catch (e) {
        this.productName = this.row.name || {};
      }
      try {
        this.productDescription = JSON.parse(this.row.description);
      } catch (e) {
        this.productDescription = this.row.description || {};
      }
      this.addedLanguages = Object.keys(this.productName).filter(lang => !!this.productName[lang]);
      if (this.addedLanguages.length > 0) {
        this.currentLanguage = this.addedLanguages[0];
      } else {
        this.currentLanguage = 'en';
        this.addedLanguages = [ 'en' ];
      }
      this.addedLanguages.forEach(lang => {
        this.languages = this.languages.filter(l => l.value !== lang);
      });
    }
  }

  protected getUrl(forceId?: number): string {
    return '/product' + (this.row.id ? `/${this.row.id}` : forceId ? `/${forceId}` : '');
  }

  submit() {
    this.formMode = (isNaN(this.row.id)) ? 'create' : 'update';
    this.form.controls.name.patchValue(JSON.stringify(this.productName));
    this.form.controls.description.patchValue(JSON.stringify(this.productDescription));
    this.form.controls.orderNumber.patchValue(+this.form.controls.orderNumber.value);
    super.submit();
  }

  protected onSubmit(response: any): void {
    Object.assign(this.row, response);
    this.isEditMode = false;
    this.toastrService.success('Saved');

    this.form.controls.name.patchValue(this.productName);
    this.form.controls.description.patchValue(this.productDescription);

    this.row.id = response.id;
  }

  onImageUploaded(image) {
    this.record.image = image.fileName;
    this.record.imageS3 = image.s3;
    this.form.controls.image.patchValue(image.fileName);
  }

  cancelImage() {
    this.record.image = '';
    this.record.imageS3 = '';
    this.form.controls.image.patchValue('');
  }

  closeLanguagePicker($event) {
    this.isLanguagePickerOpen = false;
  }

  toggleLanguagePicker($event) {
    this.isLanguagePickerOpen = true;
  }

  addLanguage(language: any) {
    this.closeLanguagePicker({});
    this.currentLanguage = language.value;
    if (this.addedLanguages.findIndex(lang => lang === language.value) === -1) {
      this.addedLanguages.push(language.value);
    }
    this.languages = this.languages.filter(l => l.value !== language.value);
    this.selectedLanguage = {};
  }

  onSelectLanguage($event) {
    this.selectedLanguage = $event;
    this.closeLanguagePicker($event);
  }

  deleteLanguage(lang: string) {
    this.productName[lang] = '';
    this.productDescription[lang] = '';
    this.form.controls.name.patchValue(this.productName);
    this.form.controls.description.patchValue(this.productDescription);

    this.addedLanguages = this.addedLanguages.filter(l => l !== lang);
    this.languages.push({ value: lang, label: Language[lang] });
  }

  editLanguage(lang: string) {
    this.form.controls.name.patchValue(this.productName);
    this.form.controls.description.patchValue(this.productDescription);

    this.currentLanguage = lang;
  }

    parseProductName(row: any): string {
        let parsedName: Record<string, string> = {};

        try {
            parsedName = JSON.parse(row.name);
        } catch (e) {
            console.warn('Could not parse:', row.name);
        }
        return parsedName['en'] || row.name;
    }
}
